import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { useSeo } from "@/hooks/useSeo";
import { news } from "@/mocks/news";

export default function NewsDetail() {
  const { id } = useParams();
  const article = news.find((n) => n.id === id);

  useSeo({
    title: article ? `${article.title} - 一本道影视大全` : "资讯详情 - 一本道影视大全",
    description: article ? article.summary : "影视资讯详情页面，提供最新行业动态与深度报道。",
    keywords: article ? `${article.category},${article.tags.join(",")},影视资讯` : "影视资讯",
  });

  if (!article) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 py-24 flex flex-col items-center justify-center text-center">
          <span className="w-20 h-20 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-600">
            <i className="ri-file-unknow-line text-4xl"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950">抱歉，该资讯不存在或已删除</h1>
          <p className="mt-3 text-sm text-foreground-500">去资讯列表看看其他精彩内容吧</p>
          <Link
            to="/news"
            className="mt-7 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回资讯列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = news.filter((n) => n.id !== article.id).slice(0, 4);

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/news" className="hover:text-primary-600 transition-colors cursor-pointer">
            影视资讯
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700 truncate max-w-[200px]">{article.title}</span>
        </nav>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          <article className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-xs">
              <span className="px-2.5 py-1 rounded bg-accent-100 text-accent-800 font-medium">{article.category}</span>
              <span className="text-foreground-400">{article.publishedAt}</span>
            </div>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-snug">
              {article.title}
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed border-l-4 border-accent-400 pl-4">
              {article.summary}
            </p>

            <div className="mt-7 w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 一本道影视大全`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="mt-8 space-y-5 text-sm md:text-base text-foreground-700 leading-loose">
              {article.content.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-8">
              {article.tags.map((t) => (
                <span key={t} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium">
                  #{t}
                </span>
              ))}
            </div>

            <div className="mt-10 pt-6 border-t border-background-200">
              <Link
                to="/news"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回资讯列表
              </Link>
            </div>
          </article>

          <aside className="w-full lg:w-80 shrink-0">
            <div className="lg:sticky lg:top-24">
              <h2 className="font-heading text-lg font-black text-foreground-950 mb-4">相关资讯</h2>
              <div className="space-y-3">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/news/detail/${item.id}`}
                    className="group flex gap-3 p-3 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="w-20 h-16 rounded-md overflow-hidden shrink-0 bg-background-200">
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title} - 一本道影视大全`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-bold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-400">{item.publishedAt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}